import { Injectable } from '@angular/core';
import { fileNameNormalizer } from '@app/shared/misc/fileNameNormalizer';
import { Observable } from 'rxjs';
import { Document } from '../entity/document';
import {
  LundifyProviderLifecycleStatus,
  LundifyShareDocumentResponse,
  LundifyShareOpenedResponse,
  LundifyShareRecipient,
  LundifySharedDocumentPage,
} from '../entity/lundify-shared-document';
import { BLService } from './bl.service';

@Injectable()
export class DocumentService extends BLService {
  private RESOURCE_ENDPOINT = 'document';

  public getFilesAndFolders(clientId: number, path: string): Observable<Document[]> {
    return this.httpPost<Document[]>(this.getFetchUrl(clientId), { fileSpec: path });
  }

  public getFile(clientId: number, path: string): Observable<BinaryType> {
    const options = this.httpAuthHeader.getHeaderWithResponseType('arraybuffer');
    options.headers.delete('Content-Type');
    return this.httpPost<BinaryType>(this.getFetchUrl(clientId), { fileSpec: path }, options);
  }

  createDirectory(clientId: number, pathToAddFolder: string, newFolderName: string): Observable<null> {
    return this.httpPost<null>(this.getAddFolderUrl(clientId), {
      fileSpec: `${pathToAddFolder}${fileNameNormalizer(newFolderName)}/`,
    });
  }

  saveFile(clientId: number, path: string, file: File): Observable<null> {
    const formData: FormData = new FormData();
    formData.append('file', file, fileNameNormalizer(file.name));
    formData.append('fileSpec', path);

    return this.httpPost<any>(this.getUploadUrl(clientId), formData, this.httpAuthHeader.getHeaderWithoutContentType());
  }

  deleteFileOrFolder(clientId: number, pathToDelete: string): Observable<null> {
    return this.httpPost<null>(this.getRemoveUrl(clientId), { fileSpec: pathToDelete });
  }

  renameFileOrFolder(clientId: number, pathToRename: string, newName: string): Observable<null> {
    const body = { fileSpec: pathToRename, newName: fileNameNormalizer(newName) };
    return this.httpPost<null>(this.getUpdateUrl(clientId), body);
  }

  // Documents the client shared from Lundify (INBOUND), fetched live via BLA polling.
  // "data" on each item is already the base64-encoded file - no extra download call needed.
  public getIncomingSharedDocuments(
    clientId: number,
    providerLifecycleStatus?: LundifyProviderLifecycleStatus,
    page = 0,
    size = 50,
  ): Observable<LundifySharedDocumentPage> {
    return this.getSharedDocuments({ clientId, direction: 'incoming', providerLifecycleStatus, page, size });
  }

  // Documents Workflow shared to the client's Lundify company (OUTBOUND).
  public getOutgoingSharedDocuments(
    clientId: number,
    providerLifecycleStatus?: LundifyProviderLifecycleStatus,
    page = 0,
    size = 50,
  ): Observable<LundifySharedDocumentPage> {
    return this.getSharedDocuments({ clientId, direction: 'outgoing', providerLifecycleStatus, page, size });
  }

  // Archives Workflow's own copy of a shared document (either direction). Independent from the
  // Lundify user's archive state and irreversible - BLA offers no unarchive.
  public archiveSharedDocument(clientId: number, documentShareId: number): Observable<void> {
    return this.httpPut<void>(this.getSharedUrl(clientId, `${documentShareId}/archive`), null);
  }

  // Provider-side access management of an already uploaded document - no file is re-sent.
  public addSharedDocumentRecipient(
    clientId: number,
    documentShareId: number,
    recipient: LundifyShareRecipient,
  ): Observable<void> {
    return this.httpPost<void>(this.getSharedUrl(clientId, `${documentShareId}/recipients`), recipient);
  }

  public changeSharedDocumentRecipientRole(
    clientId: number,
    documentShareId: number,
    cloudUserId: number,
    role: LundifyShareRecipient['role'],
  ): Observable<void> {
    return this.httpPatch<void>(this.getSharedUrl(clientId, `${documentShareId}/recipients/${cloudUserId}`), { role });
  }

  public removeSharedDocumentRecipient(clientId: number, documentShareId: number, cloudUserId: number): Observable<void> {
    return this.httpDelete<void>(this.getSharedUrl(clientId, `${documentShareId}/recipients/${cloudUserId}`));
  }

  // Shares a local file with the client's Lundify company (Workflow -> Lundify, OUTBOUND).
  // Recipients are optional: without them the file is only uploaded (BLA keeps it as NOT_SHARED)
  // and recipients are added later through the recipients endpoints (addSharedDocumentRecipient),
  // without re-sending the file. The call is idempotent per file content on BLA's side
  // (docs/design/workflow-shared-documents-api.md, section 7).
  public shareDocumentToLundify(
    clientId: number,
    file: File,
    recipients: LundifyShareRecipient[],
    path?: string,
  ): Observable<LundifyShareDocumentResponse> {
    const formData = new FormData();
    formData.append('file', file, fileNameNormalizer(file.name));
    formData.append('recipients', new Blob([JSON.stringify(recipients)], { type: 'application/json' }));
    if (path) {
      formData.append('path', new Blob([path], { type: 'text/plain' }));
    }
    return this.httpPost<LundifyShareDocumentResponse>(
      this.getSharedUrl(clientId, 'send'),
      formData,
      this.httpAuthHeader.getHeaderWithoutContentType(),
    );
  }

  // Empty "Outgoing documents" folders stored in Workflow (BLA keeps folders only as a document's
  // "path"); folders that contain documents are derived from the documents themselves.
  public getSharedFolders(clientId: number): Observable<string[]> {
    return this.httpGet<string[]>(this.getSharedUrl(clientId, 'folders'));
  }

  public createSharedFolder(clientId: number, path: string): Observable<{ path: string }> {
    return this.httpPost<{ path: string }>(this.getSharedUrl(clientId, 'folders'), { path });
  }

  public deleteSharedFolder(clientId: number, path: string): Observable<void> {
    return this.httpDelete<void>(this.getSharedUrl(clientId, `folders?${new URLSearchParams({ path })}`));
  }

  // Confirms an INBOUND shared document was opened so Lundify shows "Read" on its next poll
  // (docs/design/workflow-shared-documents-api.md, section 5). Idempotent - safe to call every
  // time the file is downloaded.
  public markIncomingDocumentOpened(clientId: number, documentShareId: number): Observable<LundifyShareOpenedResponse> {
    return this.httpPut<LundifyShareOpenedResponse>(
      this.getSharedUrl(clientId, `incoming/${documentShareId}/opened`),
      null,
    );
  }

  // Fallback download for an OUTBOUND shared document when the polling item has no inline "data"
  // (docs/design/workflow-shared-documents-api.md, section 6).
  public downloadOutgoingDocumentFile(clientId: number, reference: string): Observable<ArrayBuffer> {
    const options = this.httpAuthHeader.getHeaderWithResponseType('arraybuffer');
    return this.httpGet<ArrayBuffer>(this.getSharedUrl(clientId, `outgoing/file?${new URLSearchParams({ reference })}`), options);
  }

  private getSharedDocuments(request: {
    clientId: number;
    direction: 'incoming' | 'outgoing';
    providerLifecycleStatus?: LundifyProviderLifecycleStatus;
    page: number;
    size: number;
  }): Observable<LundifySharedDocumentPage> {
    const params = new URLSearchParams({ page: `${request.page}`, size: `${request.size}` });
    if (request.providerLifecycleStatus) {
      params.set('providerLifecycleStatus', request.providerLifecycleStatus);
    }
    return this.httpGet<LundifySharedDocumentPage>(this.getSharedUrl(request.clientId, `${request.direction}?${params}`));
  }

  private getSharedUrl(clientId: number, path: string): string {
    return this.getUrl(clientId, `shared/${path}`);
  }

  private getFetchUrl(clientId: number): string {
    return this.getUrl(clientId, 'fetch');
  }

  private getUploadUrl(clientId: number): string {
    return this.getUrl(clientId, 'upload');
  }

  private getUpdateUrl(clientId: number): string {
    return this.getUrl(clientId, 'update');
  }

  private getRemoveUrl(clientId: number): string {
    return this.getUrl(clientId, 'remove');
  }

  private getAddFolderUrl(clientId: number): string {
    return this.getUrl(clientId, 'add-folder');
  }

  private getUrl(clientId: number, path: string): string {
    return `${this.RESOURCE_ENDPOINT}/${clientId}/${path}`;
  }
}
